import {
  Dropdown,
  Flex,
  Input,
  Steps,
  Loader,
  Text,
  TextToolTip,
} from '@components';
import { Box, SelectChangeEvent, useTheme } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import InvestmentFooter from '../InvestmentFooter/InvestmentFooter';
import { ContentContainer } from './InvestmentQuestionnaire.styles';

import WithUser, { WithUserProps } from '@HOCs/withUser';
import { getPersonalDetailLockNavigation } from '../SubscriptionIntro/SubscriptionIntro.utils';
import { InvestmentContainer } from '../Investment.styles';
import { QuestionnaireFieldType } from '@features/api/response/questionnaire';
import {
  useGetCompletedQuestionnaireQuery,
  useGetQuestionnaireTemplateQuery,
  useUpdateUserQuestionnaireMutation,
} from '@features/api/questionnaireSlice';
import { useEffect, useState } from 'react';
import { QuestionOutline } from '@assets/svg';
import { TOTAL_STEPS } from '../Investment.consts';
import { Steps as ProgressSteps } from '../Progress/Progress.types';
import { KYCVerificationStatus } from '@features/api/response/User';

const InvestmentQuestionnaire: React.FC<WithUserProps> = ({ user }) => {
  const theme = useTheme();
  const intl = useIntl();
  const navigate = useNavigate();

  const { productId } = useParams();

  const {
    data: questionnaire,
    isLoading: isQuestionnaireLoading,
    isFetching: isQuestionnaireFetching,
  } = useGetQuestionnaireTemplateQuery();
  const {
    data: completedQuestionnaire,
    isLoading: isCompletedQuestionnaireLoading,
    isFetching: isCompletedQuestionnaireFetching,
  } = useGetCompletedQuestionnaireQuery(undefined, {
    skip: !user.completedQuestionnaire,
  });
  const [updateUserQuestionnaire] = useUpdateUserQuestionnaireMutation();

  const [data, setData] = useState<{ [key: string]: string[] | string }>({});

  useEffect(() => {
    if (completedQuestionnaire && questionnaire) {
      const answers = completedQuestionnaire.questionnaireFieldAnswers.reduce(
        (result: { [key: string]: string[] | string }, current) => {
          const fieldTemplate = questionnaire.fields.find(
            (x) => x.id === current.fieldId
          );

          result[current.fieldId] =
            fieldTemplate?.type === QuestionnaireFieldType.MultipleSelect
              ? current.answers
              : current.answers[0];

          return result;
        },
        {}
      );

      setData(answers);
    }
  }, [completedQuestionnaire, questionnaire]);

  const hasMissingFields = !!questionnaire?.fields?.find(
    (field) => !data[field.id]
  );
  const showLoader =
    isQuestionnaireLoading ||
    isQuestionnaireFetching ||
    isCompletedQuestionnaireLoading ||
    isCompletedQuestionnaireFetching;

  const onSubmit = async () => {
    if (!questionnaire || hasMissingFields) return;

    await updateUserQuestionnaire({
      questionnaireTemplateId: questionnaire.id,
      questionnaireFieldAnswers: questionnaire.fields.map((x) => ({
        fieldId: x.id,
        answers: Array.isArray(data[x.id])
          ? (data[x.id] as string[])
          : [data[x.id] as string],
      })),
    }).unwrap();

    if (
      user.kycVerificationStage !==
      KYCVerificationStatus.ThirdPartyChecksCompletedVerified
    ) {
      navigate(
        `/investment-subscription/${productId}/investment-profile-info`,
        {
          preventScrollReset: false,
          state: {
            progress: 7,
          },
          replace: true,
        }
      );
    } else {
      navigate(`/investment-subscription/${productId}/progress`, {
        preventScrollReset: false,
        state: {
          completedSteps: [
            ProgressSteps.Details,
            ProgressSteps.IdentityVerification,
          ],
          progress: 8,
        },
        replace: true,
      });
    }
  };

  const onChange = (value: string | string[], propName: string) => {
    if (value) {
      setData({ ...data, [propName]: Array.isArray(value) ? value : [value] });
    }
  };

  if (user.personalDetailsLocked && user.completedQuestionnaire) {
    getPersonalDetailLockNavigation(navigate, productId)();
  }

  if (showLoader) return <Loader isLoading={showLoader} />;

  return (
    <InvestmentContainer data-testid='questionnaire-container'>
      <ContentContainer>
        <Steps currentStep={7} totalSteps={TOTAL_STEPS} />
        <Box mb={4}>
          <Text
            variant='h3'
            fontWeight='fontWeightBold'
            mt={5}
            color={theme.palette.black.A100}
          >
            <FormattedMessage id='investment.questionnaire.complianceDetails' />
          </Text>
          <Text
            variant='h6'
            mt={5}
            fontWeight='fontWeightMedium'
            color={theme.palette.black[500]}
          >
            <FormattedMessage id='investment.questionnaire.complianceDetailsDescription' />
          </Text>
        </Box>
        {questionnaire?.fields.map((field) => (
          <Flex flex={1} my={4} flexDirection='column'>
            {field.hint ? (
              <TextToolTip
                text={field.title}
                toolTipText={field.hint}
                icon={() => (
                  <QuestionOutline color={theme.palette.black[200]} />
                )}
              />
            ) : (
              <Text
                variant='h6'
                fontWeight='fontWeightSemiBold'
                color={theme.palette.black.A100}
                sx={{ padding: theme.spacing(5, 0) }}
              >
                {field.title}
              </Text>
            )}
            {field.type === QuestionnaireFieldType.LongText ||
            field.type === QuestionnaireFieldType.ShortText ? (
              <Input
                label={field.placeholder}
                multiline={field.type === QuestionnaireFieldType.LongText}
                onChange={(event) =>
                  onChange(event.currentTarget.value, field.id)
                }
                value={data[field.id]}
                rows={field.type === QuestionnaireFieldType.LongText ? 4 : 1}
              />
            ) : (
              <Dropdown
                label={field.placeholder}
                multiple={field.type === QuestionnaireFieldType.MultipleSelect}
                onChange={(event: SelectChangeEvent<any>) => {
                  onChange(event.target.value, field.id);
                }}
                defaultValue={data[field.id] ?? []}
                value={data[field.id] ?? []}
                menuItemData={field.selectableValues.map(
                  (selectableValue: any) => ({
                    value: selectableValue,
                    name: selectableValue,
                  })
                )}
              />
            )}
          </Flex>
        ))}
      </ContentContainer>
      <InvestmentFooter
        secondaryButton={{
          title: intl.formatMessage({
            id: 'misc.previous',
          }),
          onClick: () =>
            user.kycVerificationStage ===
            KYCVerificationStatus.ThirdPartyChecksCompletedVerified
              ? navigate(`/investment-subscription/${productId}/amount`, {
                  state: {
                    progress: 0,
                  },
                  replace: true,
                })
              : navigate(`/investment-subscription/${productId}/bank-details`, {
                  state: {
                    progress: 5,
                  },
                  replace: true,
                }),
        }}
        primaryButton={{
          title: intl.formatMessage({
            id: 'misc.next',
          }),
          disabled: !questionnaire || hasMissingFields,
          onClick: onSubmit,
        }}
      />
    </InvestmentContainer>
  );
};

export default WithUser(InvestmentQuestionnaire);
